import { useEffect, useMemo, useRef, useState } from "react";
import { SectionTitle } from "../_shared/section-title";
import { PageShell, Stack } from "../_shared/page-shell";
import { Card } from "../_shared/card";
import {
  IconBolt,
  IconCalendar,
  IconChevronRight,
  IconClock,
  IconHelp,
  IconMail,
  IconPhone,
  IconSend,
  IconWarning,
} from "../_shared/icons";
import { back, navigate } from "../_shared/router";
import { useDemo } from "../_shared/demo.provider";
import { formatFrenchDate } from "../_shared/date-fmt";
import { children, type Child } from "../_data/mock";

const childRing = {
  orange:
    "ring-[var(--child-1-primary)] bg-[var(--child-1-light)] text-[var(--child-1-primary)]",
  blue: "ring-[var(--child-2-primary)] bg-[var(--child-2-light)] text-[var(--child-2-primary)]",
} as const;

const childPill = {
  orange: "bg-[var(--child-1-light)] text-[var(--child-1-primary)]",
  blue: "bg-[var(--child-2-light)] text-[var(--child-2-primary)]",
} as const;

const suggestions = [
  "Léger saignement de gencives, est-ce normal ?",
  "Quand puis-je reprendre le sport ?",
  "Le boîtier de droite gratte un peu.",
];

type ActionParams = {
  mode: "cancel" | "absence" | null;
  rdvId: string | null;
};

function getActionParams(): ActionParams {
  if (typeof window === "undefined") return { mode: null, rdvId: null };
  const hash = window.location.hash;
  const qIndex = hash.indexOf("?");
  if (qIndex < 0) return { mode: null, rdvId: null };
  const params = new URLSearchParams(hash.slice(qIndex + 1));
  const action = params.get("action");
  return {
    mode:
      action === "cancel" ? "cancel" : action === "absence" ? "absence" : null,
    rdvId: params.get("id"),
  };
}

function buildDraft(
  mode: ActionParams["mode"],
  rdv: ReturnType<typeof useDemo>["upcomingAppointments"][number] | null,
): string {
  if (!rdv) {
    if (mode === "absence")
      return "Bonjour,\n\nJe préviens d'une absence pour un rendez-vous à venir.\n\nRaison : ";
    return "";
  }
  const c = children.find((cc) => cc.id === rdv.childId);
  if (mode === "cancel") {
    return `Bonjour,\n\nJe souhaite annuler le rendez-vous prévu le ${formatFrenchDate(
      rdv.isoDate,
    )} à ${rdv.time}${c ? ` pour ${c.firstName}` : ""} (${rdv.kind}).\n\nMotif : `;
  }
  if (mode === "absence") {
    return `Bonjour,\n\nJe préviens d'une absence pour le rendez-vous du ${formatFrenchDate(
      rdv.isoDate,
    )} à ${rdv.time}${c ? ` pour ${c.firstName}` : ""}.\n\nRaison : `;
  }
  return "";
}

export function ContactPage() {
  const { upcomingAppointments, cancelAppointment } = useDemo();

  const initialParams = useMemo(getActionParams, []);
  const [mode, setMode] = useState<ActionParams["mode"]>(initialParams.mode);
  const [selectedRdvId, setSelectedRdvId] = useState<string | null>(
    initialParams.rdvId,
  );
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const targetRdv = selectedRdvId
    ? (upcomingAppointments.find((a) => a.id === selectedRdvId) ?? null)
    : null;

  const initialChild = targetRdv?.childId ?? children[0]?.id ?? "jeanne";

  const [childId, setChildId] = useState<Child["id"]>(initialChild);
  const [message, setMessage] = useState<string>(() =>
    buildDraft(initialParams.mode, null),
  );
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Quand un RDV est sélectionné (manuellement ou via l'URL), on régénère
  // le brouillon en l'injectant dans le textarea — seulement si l'utilisateur
  // n'a pas encore tapé son propre texte (longueur égale à un brouillon vide).
  useEffect(() => {
    if (mode && targetRdv) {
      setMessage(buildDraft(mode, targetRdv));
    } else if (mode === "absence" && !targetRdv) {
      setMessage(buildDraft("absence", null));
    } else if (mode === "cancel" && !targetRdv) {
      setMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, targetRdv?.id]);

  useEffect(() => {
    if (mode && targetRdv && textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [mode, targetRdv?.id]);

  const submit = () => {
    if (!message.trim()) return;
    if (mode === "cancel" && targetRdv) {
      cancelAppointment(targetRdv.id);
      setConfirmation(
        `Annulation envoyée au cabinet pour le rendez-vous du ${formatFrenchDate(
          targetRdv.isoDate,
        )}.`,
      );
    } else if (mode === "absence" && targetRdv) {
      setConfirmation(
        `Absence signalée pour le rendez-vous du ${formatFrenchDate(
          targetRdv.isoDate,
        )}.`,
      );
    } else if (mode === "absence") {
      setConfirmation("Message d'absence envoyé au cabinet.");
    } else {
      setConfirmation("Message envoyé au cabinet.");
    }
    setMessage("");
    setMode(null);
    setTimeout(() => {
      back();
    }, 1500);
  };

  const isCancelOrAbsence = mode === "cancel" || mode === "absence";

  return (
    <>
      <SectionTitle
        title={
          mode === "cancel"
            ? "Annuler un rendez-vous"
            : mode === "absence"
              ? "Prévenir d'une absence"
              : "Contact"
        }
      />
      <PageShell>
        <Stack>
          {confirmation ? (
            <div className="rounded-3xl bg-[var(--sf-primary-100)] p-4 text-[14px] font-semibold text-[var(--sf-primary)]">
              {confirmation}
            </div>
          ) : null}

          {mode === "cancel" && targetRdv ? (
            <CancelBanner rdv={targetRdv} />
          ) : null}

          {mode === "absence" && targetRdv ? (
            <AbsenceBanner rdv={targetRdv} />
          ) : null}

          {isCancelOrAbsence && !targetRdv ? (
            <AppointmentPicker
              appointments={upcomingAppointments}
              onPick={setSelectedRdvId}
              mode={mode}
            />
          ) : null}

          {!isCancelOrAbsence ? (
            <>
              <Card title="Concerne">
                <ul className="flex flex-wrap gap-2 pt-1">
                  {children.map((c) => {
                    const selected = childId === c.id;
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => setChildId(c.id)}
                          className={`inline-flex h-10 items-center rounded-full px-4 text-[14px] font-semibold ring-1 transition ${selected ? childRing[c.color] : "bg-[var(--surface-1)] text-[var(--text-2)] ring-[var(--border-1)]"}`}
                        >
                          {c.firstName}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Card>

              <div className="grid grid-cols-2 gap-3">
                <ActionTile
                  icon={IconBolt}
                  label="Photo d'urgence"
                  description="Bague, fil, douleur…"
                  onClick={() => navigate("emergency")}
                />
                <ActionTile
                  icon={IconCalendar}
                  label="Annuler un RDV"
                  description="Délai 24h minimum"
                  onClick={() => navigate("appointments")}
                />
                <ActionTile
                  icon={IconClock}
                  label="Prévenir d'une absence"
                  description="Pour un rendez-vous à venir"
                  onClick={() => setMode("absence")}
                />
                <ActionTile
                  icon={IconHelp}
                  label="Demander une info"
                  description="Soins, hygiène, traitement"
                  onClick={() => {
                    textareaRef.current?.focus();
                  }}
                />
              </div>

              <Card title="Joindre directement">
                <ul className="-mx-1 divide-y divide-[var(--border-1)]">
                  <li>
                    <a
                      href="tel:+33102030405"
                      className="flex items-center gap-3 px-1 py-2.5"
                    >
                      <span className="inline-flex size-10 items-center justify-center rounded-2xl bg-[var(--sf-primary-100)] text-[var(--sf-primary)]">
                        <IconPhone width={18} height={18} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-semibold text-[var(--text-1)]">
                          Appeler le secrétariat
                        </p>
                        <p className="text-[12.5px] text-[var(--text-2)]">
                          01 02 03 04 05 — Lun–Ven 9h–19h
                        </p>
                      </div>
                      <IconChevronRight
                        width={16}
                        height={16}
                        className="text-[var(--text-3)]"
                      />
                    </a>
                  </li>
                  <li>
                    <a
                      href="mailto:cabinet@drrenoud.fr"
                      className="flex items-center gap-3 px-1 py-2.5"
                    >
                      <span className="inline-flex size-10 items-center justify-center rounded-2xl bg-[var(--sf-primary-100)] text-[var(--sf-primary)]">
                        <IconMail width={18} height={18} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-semibold text-[var(--text-1)]">
                          Email
                        </p>
                        <p className="truncate text-[12.5px] text-[var(--text-2)]">
                          cabinet@drrenoud.fr
                        </p>
                      </div>
                      <IconChevronRight
                        width={16}
                        height={16}
                        className="text-[var(--text-3)]"
                      />
                    </a>
                  </li>
                </ul>
              </Card>
            </>
          ) : null}

          {isCancelOrAbsence && !targetRdv ? null : (
            <Card
              title={
                mode === "cancel"
                  ? "Message d'annulation"
                  : mode === "absence"
                    ? "Message d'absence"
                    : "Écrire au cabinet"
              }
            >
              {!isCancelOrAbsence ? (
                <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setMessage(s)}
                      className="inline-flex shrink-0 items-center rounded-full bg-[var(--surface-2)] px-3.5 py-1.5 text-[12.5px] font-medium text-[var(--text-2)] transition active:scale-95"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="flex items-end gap-2 rounded-2xl bg-[var(--surface-2)] p-2.5 ring-1 ring-[var(--border-1)] focus-within:ring-2 focus-within:ring-[var(--sf-primary)]">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={isCancelOrAbsence ? 7 : 3}
                  placeholder={
                    isCancelOrAbsence
                      ? "Précisez le motif…"
                      : "Bonjour, j'aurais une question…"
                  }
                  className="block min-h-7 w-full resize-none bg-transparent outline-none placeholder:text-[var(--text-3)]"
                />
                {!isCancelOrAbsence ? (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={!message.trim()}
                    aria-label="Envoyer"
                    className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--sf-primary)] text-white shadow-sm transition active:scale-95 disabled:bg-[var(--surface-3)] disabled:text-[var(--text-3)]"
                  >
                    <IconSend width={18} height={18} strokeWidth={2} />
                  </button>
                ) : null}
              </div>
              {isCancelOrAbsence ? (
                <button
                  type="button"
                  onClick={submit}
                  disabled={!message.trim()}
                  className="mt-3 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[var(--sf-primary)] px-5 text-[15px] font-semibold text-white shadow-sm transition active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-[var(--surface-3)] disabled:text-[var(--text-3)] sm:mx-auto sm:max-w-sm"
                >
                  <IconSend width={16} height={16} />
                  {mode === "cancel"
                    ? "Confirmer l'annulation"
                    : "Envoyer l'absence"}
                </button>
              ) : null}
              <p className="mt-2.5 text-[11.5px] text-[var(--text-2)]">
                Réponse en moyenne sous 4 heures (jours ouvrés).
              </p>
            </Card>
          )}
        </Stack>
      </PageShell>
    </>
  );
}

function AppointmentPicker({
  appointments,
  onPick,
  mode,
}: {
  appointments: ReturnType<typeof useDemo>["upcomingAppointments"];
  onPick: (id: string) => void;
  mode: "cancel" | "absence";
}) {
  if (appointments.length === 0) {
    return (
      <Card title="Aucun rendez-vous à venir">
        <p className="text-[13px] text-[var(--text-2)]">
          {mode === "cancel"
            ? "Vous n'avez aucun rendez-vous à annuler pour l'instant."
            : "Aucun rendez-vous à venir — pas besoin de prévenir d'absence."}
        </p>
      </Card>
    );
  }
  return (
    <Card
      title={
        mode === "cancel"
          ? "Choisir le rendez-vous à annuler"
          : "Pour quel rendez-vous ?"
      }
      multilineTitle
    >
      <ul className="-mx-1 divide-y divide-[var(--border-1)]">
        {appointments.map((rdv) => {
          const child = children.find((c) => c.id === rdv.childId);
          return (
            <li key={rdv.id}>
              <button
                type="button"
                onClick={() => onPick(rdv.id)}
                className="flex w-full items-center gap-3 px-1 py-2.5 text-left transition active:scale-[0.99]"
              >
                <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-2xl bg-[var(--sf-primary-100)] text-[var(--sf-primary)]">
                  <IconCalendar width={18} height={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-[var(--text-1)]">
                    {rdv.kind}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12.5px] text-[var(--text-2)]">
                    <span className="first-letter:uppercase">
                      {formatFrenchDate(rdv.isoDate)}
                    </span>
                    <span aria-hidden>·</span>
                    <span>{rdv.time}</span>
                    {child ? (
                      <span
                        className={`inline-flex h-4.5 items-center rounded-full px-1.5 text-[10.5px] font-semibold ${childPill[child.color]}`}
                      >
                        {child.firstName}
                      </span>
                    ) : null}
                  </p>
                </div>
                <IconChevronRight
                  width={16}
                  height={16}
                  className="text-[var(--text-3)]"
                />
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function ActionTile({
  icon: Icon,
  label,
  description,
  onClick,
}: {
  icon: typeof IconBolt;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-start gap-2 rounded-3xl bg-[var(--surface-1)] p-4 text-left shadow-[0_1px_3px_rgba(15,23,42,0.04),0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-[var(--border-1)] transition active:scale-[0.99]"
    >
      <span className="inline-flex size-11 items-center justify-center rounded-2xl bg-[var(--sf-primary-100)] text-[var(--sf-primary)]">
        <Icon width={22} height={22} />
      </span>
      <span className="block text-[14.5px] font-bold leading-tight text-[var(--text-1)]">
        {label}
      </span>
      <span className="text-[12px] leading-snug text-[var(--text-2)]">
        {description}
      </span>
    </button>
  );
}

function CancelBanner({
  rdv,
}: {
  rdv: {
    id: string;
    childId: string;
    isoDate: string;
    time: string;
    kind: string;
  };
}) {
  const child = children.find((c) => c.id === rdv.childId);
  return (
    <Card title="Rendez-vous à annuler">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-2xl bg-[var(--sf-primary-100)] text-[var(--sf-primary)]">
          <IconWarning width={20} height={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold text-[var(--text-1)]">
            {rdv.kind}
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[13px] text-[var(--text-2)]">
            <span className="first-letter:uppercase">
              {formatFrenchDate(rdv.isoDate)}
            </span>
            <span aria-hidden>·</span>
            <span>{rdv.time}</span>
            {child ? (
              <span
                className={`inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold ${childPill[child.color as "orange" | "blue"]}`}
              >
                {child.firstName}
              </span>
            ) : null}
          </p>
          <p className="mt-2 text-[12px] text-[var(--text-3)]">
            Annulation possible jusqu'à 24h avant le rendez-vous.
          </p>
        </div>
      </div>
    </Card>
  );
}

function AbsenceBanner({
  rdv,
}: {
  rdv: {
    id: string;
    childId: string;
    isoDate: string;
    time: string;
    kind: string;
  };
}) {
  const child = children.find((c) => c.id === rdv.childId);
  return (
    <Card title="Rendez-vous concerné">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-2xl bg-[var(--sf-primary-100)] text-[var(--sf-primary)]">
          <IconClock width={20} height={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold text-[var(--text-1)]">
            {rdv.kind}
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[13px] text-[var(--text-2)]">
            <span className="first-letter:uppercase">
              {formatFrenchDate(rdv.isoDate)}
            </span>
            <span aria-hidden>·</span>
            <span>{rdv.time}</span>
            {child ? (
              <span
                className={`inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold ${childPill[child.color as "orange" | "blue"]}`}
              >
                {child.firstName}
              </span>
            ) : null}
          </p>
        </div>
      </div>
    </Card>
  );
}
